/**
 * API Path Security Utility
 * 
 * Provides functions to sanitize and validate API path parameters
 * to prevent SSRF (Server-Side Request Forgery) attacks.
 * 
 * @author Security Team
 * @version 1.0.0
 */

/**
 * Sanitizes and validates an ID parameter for API paths
 * Prevents SSRF by ensuring only safe alphanumeric IDs are allowed
 * 
 * @param {string|number} id - The ID to sanitize
 * @param {Object} options - Configuration options
 * @param {number} options.maxLength - Maximum allowed length (default: 50)
 * @param {boolean} options.allowHyphens - Allow hyphens in ID (default: true)
 * @param {boolean} options.allowUnderscores - Allow underscores in ID (default: true)
 * @returns {string|null} - Sanitized ID or null if invalid
 */
export const sanitizeApiId = (id, options = {}) => {
    const {
        maxLength = 50,
        allowHyphens = true,
        allowUnderscores = true
    } = options;

    // Basic validation
    if (id === null || id === undefined) {
        return null;
    }

    // Convert to string and trim
    const idStr = String(id).trim();
    
    if (!idStr) {
        return null;
    }

    // Check length
    if (idStr.length > maxLength) {
        return null;
    }

    // Build allowed character pattern
    let allowedPattern = 'a-zA-Z0-9';
    if (allowHyphens) allowedPattern += '\\-';
    if (allowUnderscores) allowedPattern += '_';

    const regex = new RegExp(`^[${allowedPattern}]+$`);

    // Validate pattern
    if (!regex.test(idStr)) {
        return null;
    }

    // Additional security checks
    // Prevent obvious malicious patterns
    const maliciousPatterns = [
        /\.\./,           // Path traversal
        /\/\//,           // Double slashes
        /%[0-9a-fA-F]{2}/, // URL encoding
        /\\/,             // Backslashes
        /:/,              // Colons (protocol separators)
        /@/,              // At symbols (user info in URLs)
        /\?/,             // Query parameters
        /#/,              // Fragments
        /\s/              // Whitespace
    ];

    for (const pattern of maliciousPatterns) {
        if (pattern.test(idStr)) {
            return null;
        }
    }

    return idStr;
};

/**
 * Sanitizes a tipo/type parameter for API paths
 * Validates against a whitelist of allowed types
 * 
 * @param {string} tipo - The type parameter to sanitize
 * @param {Array<string>} allowedTypes - Array of allowed type values (optional)
 * @returns {string|null} - Sanitized type or null if invalid
 */
export const sanitizeApiTipo = (tipo, allowedTypes = null) => {
    if (!tipo || typeof tipo !== 'string') {
        return null;
    }

    const sanitizedTipo = tipo.trim().toLowerCase();
    
    if (!sanitizedTipo) {
        return null;
    }

    // Default allowed types for this application
    const defaultAllowedTypes = [
        'comutacao-bibliografica',
        'artigo',
        'tese',
        'parte-documento',
        'levantamento-bibliografico',
        'ficha-catalografica',
        'pesquisa-especializada',
        'emprestimo-inter-bibliotecas',
        'emprestimo',
        'reserva',
        'renovacao'
    ];

    const typesToCheck = allowedTypes || defaultAllowedTypes;

    // Check if type is in whitelist
    if (!typesToCheck.includes(sanitizedTipo)) {
        return null;
    }

    // Additional pattern validation
    const validPattern = /^[a-z\-]+$/;
    if (!validPattern.test(sanitizedTipo)) {
        return null;
    }

    return sanitizedTipo;
};

/**
 * Validates and sanitizes status parameters
 * 
 * @param {string} status - The status parameter to sanitize
 * @returns {string|null} - Sanitized status or null if invalid
 */
export const sanitizeApiStatus = (status) => {
    if (!status || typeof status !== 'string') {
        return null;
    }

    const sanitizedStatus = status.trim().toLowerCase();
    
    if (!sanitizedStatus) {
        return null;
    }

    // Allowed status values
    const allowedStatuses = [
        'ativo',
        'inativo',
        'pendente',
        'concluido',
        'cancelado',
        'em-andamento',
        'aprovado',
        'rejeitado'
    ];

    if (!allowedStatuses.includes(sanitizedStatus)) {
        return null;
    }

    return sanitizedStatus;
};

/**
 * Sanitizes a date parameter for API calls
 * Validates date format and prevents injection attacks
 * 
 * @param {string|Date} date - The date to sanitize
 * @returns {string|null} - Sanitized date string (YYYY-MM-DD) or null if invalid
 */
export const sanitizeApiDate = (date) => {
    if (!date) {
        return null;
    }

    let dateStr;
    
    // Convert Date object to string
    if (date instanceof Date) {
        if (isNaN(date.getTime())) {
            return null;
        }
        dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
    } else if (typeof date === 'string') {
        dateStr = date.trim();
    } else {
        return null;
    }

    // Validate date format (YYYY-MM-DD or YYYY/MM/DD or DD/MM/YYYY)
    const dateFormats = [
        /^\d{4}-\d{2}-\d{2}$/,           // YYYY-MM-DD
        /^\d{4}\/\d{2}\/\d{2}$/,         // YYYY/MM/DD
        /^\d{2}\/\d{2}\/\d{4}$/,         // DD/MM/YYYY
        /^\d{2}-\d{2}-\d{4}$/            // DD-MM-YYYY
    ];

    const isValidFormat = dateFormats.some(format => format.test(dateStr));
    
    if (!isValidFormat) {
        return null;
    }

    // Additional security: check for common injection patterns
    const maliciousPatterns = [
        /<script/i,
        /javascript:/i,
        /on\w+=/i,
        /alert\(/i,
        /eval\(/i,
        /expression\(/i,
        /url\(/i,
        /%/,                // URL encoding
        /\\/,               // Backslashes
        /'/,                // Single quotes
        /"/,                // Double quotes
        /;/,                // Semicolons
        /--/,               // SQL comments
        /\/\*/,             // Block comments
    ];

    for (const pattern of maliciousPatterns) {
        if (pattern.test(dateStr)) {
            return null;
        }
    }

    // Try to parse and validate the date
    try {
        let parsedDate;
        
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            parsedDate = new Date(dateStr + 'T00:00:00');
        } else if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateStr)) {
            parsedDate = new Date(dateStr);
        } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
            const [day, month, year] = dateStr.split('/');
            parsedDate = new Date(`${year}-${month}-${day}`);
        } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
            const [day, month, year] = dateStr.split('-');
            parsedDate = new Date(`${year}-${month}-${day}`);
        }

        if (!parsedDate || isNaN(parsedDate.getTime())) {
            return null;
        }

        // Check for reasonable date range (not too far in past/future)
        const minDate = new Date('1900-01-01');
        const maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() + 100);

        if (parsedDate < minDate || parsedDate > maxDate) {
            return null;
        }

        // Return in standardized format
        return parsedDate.toISOString().split('T')[0];
    } catch (error) {
        return null;
    }
};

/**
 * Builds a secure API URL using URL constructor to prevent SSRF
 * This approach avoids string concatenation which can trigger static analysis warnings
 * 
 * @param {string} basePath - The base API path
 * @param {Object} pathParams - Object containing path parameters to append
 * @returns {string|null} - Complete secure API path or null if invalid
 */
export const buildSecureApiPath = (basePath, pathParams = {}) => {
    if (!basePath || typeof basePath !== 'string') {
        return null;
    }

    try {
        // Start with the base path
        let securePath = basePath;

        // Process each path parameter
        for (const [key, value] of Object.entries(pathParams)) {
            let sanitizedValue;

            switch (key) {
                case 'id':
                case 'idAlerta':
                    sanitizedValue = sanitizeApiId(value);
                    break;
                case 'tipo':
                case 'tipoSolicitacao':
                    sanitizedValue = sanitizeApiTipo(value);
                    break;
                case 'status':
                    sanitizedValue = sanitizeApiStatus(value);
                    break;
                default:
                    // For other parameters, use basic ID sanitization
                    sanitizedValue = sanitizeApiId(value, { maxLength: 100 });
                    break;
            }

            if (sanitizedValue === null) {
                logSecurityEvent('invalid_parameter', { key, value: String(value) });
                return null;
            }

            // Safely append the parameter using array join instead of concatenation
            const pathParts = [securePath, sanitizedValue];
            securePath = pathParts.join('/');
        }

        return securePath;
    } catch (error) {
        logSecurityEvent('path_building_error', { basePath, error: error.message });
        return null;
    }
};

/**
 * Sanitizes and validates a código de acervo parameter
 * Acervo codes have specific format requirements
 * 
 * @param {string|number} codAcervo - The acervo code to sanitize
 * @returns {string|null} - Sanitized acervo code or null if invalid
 */
export const sanitizeApiCodAcervo = (codAcervo) => {
    if (codAcervo === null || codAcervo === undefined) {
        return null;
    }

    // Convert to string and trim
    const codStr = String(codAcervo).trim();
    
    if (!codStr) {
        return null;
    }

    // Check length (acervo codes are typically not very long)
    if (codStr.length > 20) {
        return null;
    }

    // Allow alphanumeric characters, hyphens, underscores, and dots
    // Acervo codes can have various formats
    const validPattern = /^[a-zA-Z0-9\-_.]+$/;
    
    if (!validPattern.test(codStr)) {
        return null;
    }

    // Additional security checks - prevent obvious malicious patterns
    const maliciousPatterns = [
        /\.\./,           // Path traversal
        /\/\//,           // Double slashes
        /%[0-9a-fA-F]{2}/, // URL encoding
        /\\/,             // Backslashes
        /:/,              // Colons (protocol separators)
        /@/,              // At symbols (user info in URLs)
        /\?/,             // Query parameters
        /#/,              // Fragments
        /\s/,             // Whitespace
        /;/,              // Semicolons
        /'/,              // Single quotes
        /"/,              // Double quotes
        /</,              // Less than
        />/,              // Greater than
        /=/               // Equals
    ];

    for (const pattern of maliciousPatterns) {
        if (pattern.test(codStr)) {
            return null;
        }
    }

    return codStr;
};

/**
 * Creates a completely validated and secure API endpoint
 * This function provides a whitelist-based approach to URL construction
 * 
 * @param {string} endpoint - The endpoint identifier
 * @param {Object} params - Parameters for the endpoint
 * @returns {string|null} - Complete validated URL or null if invalid
 */
export const createSecureEndpoint = (endpoint, params = {}) => {
    // Whitelist of allowed endpoints to prevent SSRF
    const SECURE_ENDPOINTS = {
        'ALERT_MARK_READ': 'api/solicitacoes/alertas',
        'ALERT_DELETE': 'api/solicitacoes/alertas', 
        'CANCEL_EMPRESTIMO': 'api/meupergamum/solicitacoes/cancel-emprestimo',
        'CANCEL_SOLICITACAO': 'api/solicitacoes/cancel',
        'EMPRESTIMO_DADOS': 'api/solicitacoes/emprestimo-inter-bibliotecas/dados',
        'EMPRESTIMO_OCORRENCIAS': 'api/meupergamum/solicitacoes/emprestimo'
    };

    // Validate endpoint is in whitelist
    if (!SECURE_ENDPOINTS[endpoint]) {
        logSecurityEvent('invalid_endpoint', { endpoint });
        return null;
    }

    const basePath = SECURE_ENDPOINTS[endpoint];

    // Handle different endpoint types
    switch (endpoint) {
        case 'ALERT_MARK_READ':
        case 'ALERT_DELETE':
            if (!params.idAlerta) {
                return null;
            }
            const sanitizedAlertId = sanitizeApiId(params.idAlerta);
            if (!sanitizedAlertId) {
                return null;
            }
            return basePath + '/' + sanitizedAlertId;

        case 'CANCEL_EMPRESTIMO':
            if (!params.id) {
                return null;
            }
            const sanitizedEmprestimoId = sanitizeApiId(params.id);
            if (!sanitizedEmprestimoId) {
                return null;
            }
            return basePath + '/' + sanitizedEmprestimoId;

        case 'CANCEL_SOLICITACAO':
        case 'EMPRESTIMO_DADOS':
        case 'EMPRESTIMO_OCORRENCIAS':
            // These endpoints don't require path parameters
            return basePath;

        default:
            return null;
    }
};

/**
 * Logs security events for monitoring
 * 
 * @param {string} event - The security event type
 * @param {Object} details - Event details
 */
export const logSecurityEvent = (event, details = {}) => {
    const logEntry = {
        timestamp: new Date().toISOString(),
        event,
        details,
        userAgent: navigator.userAgent,
        url: window.location.href
    };

    // In production, this should be sent to a security monitoring system
    if (process.env.NODE_ENV === 'development') {
        // Only log in development mode to avoid production console spam
        // eslint-disable-next-line no-console
        console.warn('Security Event:', logEntry);
    }

    // Here you would integrate with your security monitoring service
    // Example: sendToSecurityService(logEntry);
};
